import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageCircle, Sparkles, BookOpen, LogOut, Orbit, Headphones, Home, Flame, Compass, Puzzle, Users, Megaphone, Settings, CreditCard, Menu, X, Feather, Swords, Scale, FlameKindling, Sunrise, Telescope, Circle, Navigation, Pin, PinOff, HeartPulse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProfiles } from "@/context/ProfileContext";
import { ProfileAvatar } from "@/components/family/ProfileEditor";
import { ft } from "@/lib/familyText";
import { api } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SpiralCinematic } from "@/components/SpiralCinematic";
import FilDeCuriosite from "@/components/FilDeCuriosite";
import Boussole from "@/components/Boussole";import Constellation from "@/components/Constellation";
import Carnet from "@/components/Carnet";
import MesPodcasts from "@/components/MesPodcasts";
import { MonAbonnement } from "@/components/MonAbonnement";
import Accueil from "@/components/Accueil";
import Campfire from "@/components/Campfire";
import Domaines from "@/components/Domaines";
import JeuDeLEsprit from "@/components/JeuDeLEsprit";
import DuoCuriosite from "@/components/DuoCuriosite";
import CercleLaunch from "@/components/CercleLaunch";
import DeuxVoies from "@/components/DeuxVoies";
import ProcesIdees from "@/components/ProcesIdees";
import FeuDuDoute from "@/components/FeuDuDoute";
import TroisAges from "@/components/TroisAges";
import Explorateur from "@/components/Explorateur";
import Onboarding from "@/components/Onboarding";
import TermsUpdateModal from "@/components/TermsUpdateModal";
import { CocoonMark } from "@/components/CocoonMark";
import { WelcomeGift } from "@/components/surprises/WelcomeGift";
import { AnniversaryModal } from "@/components/surprises/AnniversaryModal";
import { CoconMoodCheckin } from "@/components/CoconMoodCheckin";
import { CoconHistory } from "@/components/CoconHistory";

export default function Atelier() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("accueil");
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [starCounts, setStarCounts] = useState({});
  const [streak, setStreak] = useState(null);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [pins, setPins] = useState([]);
  const [moodChecked, setMoodChecked] = useState(false);
  const { user, logout, refresh } = useAuth();
  const { activeProfile, clearActive } = useProfiles();
  const navigate = useNavigate();

  const finishOnboarding = async (answer) => {
    await refresh();
    if (answer) {
      setPendingPrompt({ text: answer, ts: Date.now() });
      setTab("fil");
    }
  };

  const TABS = [
    { id: "accueil", label: t("atelier.accueil"), icon: Home },
    { id: "fil", label: t("atelier.fil"), icon: MessageCircle, highlight: true },
    { id: "cercle", label: ({ fr: "Le Cercle", en: "The Circle", es: "El Círculo", nl: "De Cirkel" }[(i18n.language || "fr").slice(0, 2)]) || "Le Cercle", icon: Circle, highlight: true },
    { id: "boussole", label: ({ fr: "La Boussole de l'Inattendu", en: "The Compass of the Unexpected", es: "La Brújula de lo Inesperado", nl: "Het Kompas van het Onverwachte" }[(i18n.language || "fr").slice(0, 2)]) || "La Boussole de l'Inattendu", icon: Navigation, highlight: true },
    { id: "constellation", label: t("atelier.constellation"), icon: Sparkles },
    { id: "cocon", label: ({ fr: "Histoire de ton Cocon", en: "Your Cocoon's story", es: "La historia de tu Cocoon", nl: "Verhaal van je Cocoon" }[(i18n.language || "fr").slice(0, 2)]) || "Histoire de ton Cocon", icon: HeartPulse },
    { id: "carnet", label: t("atelier.carnet"), icon: BookOpen },
    { id: "campfire", label: t("atelier.campfire"), icon: Flame },
    { id: "duo", label: t("atelier.duoTab"), icon: Users },
    { id: "deuxVoies", label: t("atelier.deuxVoies"), icon: Swords },
    { id: "proces", label: t("atelier.proces"), icon: Scale },
    { id: "doute", label: t("atelier.doute"), icon: FlameKindling },
    { id: "verites", label: t("atelier.verites"), icon: Sunrise },
    { id: "expedition", label: t("atelier.expedition"), icon: Telescope },
    { id: "jeux", label: t("games.navLabel"), icon: Puzzle },
    { id: "podcasts", label: t("atelier.podcasts"), icon: Headphones },
    { id: "abonnement", label: t("atelier.abonnement"), icon: CreditCard },
  ];

  useEffect(() => {
    api.get("/constellation").then(({ data }) => {
      const counts = {};
      data.nodes.forEach((n) => {
        counts[n.category] = (counts[n.category] || 0) + (n.count || 1);
      });
      setStarCounts(counts);
    }).catch(() => {});
    api.get("/streak").then(({ data }) => setStreak(data)).catch(() => {});
  }, [tab]);

  useEffect(() => {
    const p = localStorage.getItem("coccon_pending_prompt");
    if (p) {
      localStorage.removeItem("coccon_pending_prompt");
      setPendingPrompt({ text: p, ts: Date.now() });
      setTab("fil");
      return;
    }
    const pt = localStorage.getItem("coccon_pending_tab");
    if (pt) {
      localStorage.removeItem("coccon_pending_tab");
      setTab(pt);
    }
  }, []);

  useEffect(() => {
    api.get("/referral/notice").then(({ data }) => {
      if (data.days > 0) {
        toast.success(t("atelier.rewardNoticeTitle"), { description: t("atelier.rewardNotice", { count: data.days }) });
      }
    }).catch(() => {});
    if (user?.role === "admin") {
      api.get("/admin/messages/unread").then(({ data }) => setUnreadMsgs(data.unread || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const explore = (prompt) => {
    setPendingPrompt({ text: prompt, ts: Date.now() });
    setTab("fil");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    api.get("/pins").then(({ data }) => setPins(data.pins || [])).catch(() => {});
  }, []);

  // Tools that can be pinned (everything except the home tab itself).
  const PINNABLE = TABS.filter((tItem) => tItem.id !== "accueil");
  const toolMeta = Object.fromEntries(TABS.map((tItem) => [tItem.id, { label: tItem.label, icon: tItem.icon }]));
  const isPinnable = tab !== "accueil" && PINNABLE.some((tItem) => tItem.id === tab);
  const isPinned = pins.includes(tab);

  const togglePin = async (toolId) => {
    const id = toolId || tab;
    if (!id || id === "accueil") return;
    // optimistic
    const was = pins.includes(id);
    setPins((prev) => (was ? prev.filter((p) => p !== id) : [...prev, id]));
    try {
      const { data } = await api.post("/pins/toggle", { tool_id: id });
      setPins(data.pins || []);
      toast.success(data.pinned ? t("atelier.pins.added") : t("atelier.pins.removed"));
    } catch (e) {
      setPins((prev) => (was ? [...prev, id] : prev.filter((p) => p !== id)));
      const msg = e?.response?.data?.detail || t("atelier.pins.error");
      toast.error(msg);
    }
  };

  const pinnedTools = pins
    .filter((id) => toolMeta[id])
    .map((id) => ({ id, label: toolMeta[id].label, icon: toolMeta[id].icon }));

  return (
    <div className="relative flex flex-col bg-background overflow-hidden" style={{ height: "var(--vvh, 100dvh)" }}>
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-14%", left: "50%", transform: "translateX(-50%)", width: "820px", height: "560px", opacity: 0.7 }} />
      <AnimatePresence>
        {user && user.onboarded === false && <Onboarding onDone={finishOnboarding} />}
      </AnimatePresence>
      <AnimatePresence>
        {user && user.onboarded !== false && user.terms_ok === false && (
          <TermsUpdateModal onAccepted={refresh} />
        )}
      </AnimatePresence>
      {user && user.onboarded !== false && user.terms_ok !== false && (
        <>
          <WelcomeGift />
          <AnniversaryModal />
          {!moodChecked && <CoconMoodCheckin onClose={() => setMoodChecked(true)} onExplore={(topic) => { setPendingPrompt({ text: topic, ts: Date.now() }); setTab("fil"); setMoodChecked(true); }} />}
        </>
      )}
      {/* top bar */}
      <header className="glass z-30 flex-shrink-0">
        <div className="px-6 h-16 flex items-center justify-between">
          <span className="font-display text-2xl tracking-tight" data-testid="atelier-brand"><CocoonMark withMind /></span>
          <div className="flex items-center gap-4">
            {streak && (
              <button
                onClick={() => setTab("accueil")}
                data-testid="streak-badge"
                title={t("atelier.streakTitle")}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full frame-pulse hover:bg-primary/5 transition-colors"
              >
                <Flame size={15} className={streak.streak > 0 ? "text-orange-400" : "text-foreground/40"} />
                <span className="text-sm text-foreground/80 tabular-nums">{streak.streak}</span>
                <span className={`text-xs tabular-nums ${streak.on_track ? "text-emerald-400" : "text-orange-400"}`}>{streak.score}%</span>
              </button>
            )}
            <LanguageSwitcher className="flex" />
            {activeProfile && (
              <button
                data-testid="atelier-profile-switch"
                onClick={() => clearActive()}
                title={ft().switch}
                className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-full border border-white/12 hover:border-primary/40 transition-colors"
              >
                <ProfileAvatar profile={activeProfile} size={30} />
                <span className="hidden sm:inline text-sm text-foreground/70 max-w-[90px] truncate">{activeProfile.name}</span>
              </button>
            )}
            <button
              data-testid="menu-toggle-btn"
              onClick={() => setMenuOpen(true)}
              title="Menu"
              className="relative flex items-center justify-center w-10 h-10 rounded-full frame-pulse text-primary hover:bg-primary/10 transition-colors"
            >
              <Menu size={20} />
              {user?.role === "admin" && unreadMsgs > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadMsgs > 9 ? "9+" : unreadMsgs}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* hamburger drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm"
              data-testid="menu-backdrop"
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-[86vw] max-w-[340px] glass border-l border-primary/20 flex flex-col"
              data-testid="menu-drawer"
            >
              <div className="flex items-center justify-between px-6 h-16 flex-shrink-0 border-b border-white/5">
                <span className="font-display text-xl"><CocoonMark withMind /></span>
                <button
                  data-testid="menu-close-btn"
                  onClick={() => setMenuOpen(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-foreground/60 hover:text-primary hover:bg-primary/10 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {user?.name && (
                <p className="px-6 pt-4 text-sm text-foreground/50 font-display italic flex-shrink-0">{user.name}</p>
              )}

              <nav className="flex-1 min-h-0 overflow-y-auto scroll-thin px-3 py-4 space-y-1">
                {TABS.map((item) => {
                  const Icon = item.icon;
                  const active = tab === item.id;
                  const hl = item.highlight;
                  return (
                    <button
                      key={item.id}
                      data-testid={`menu-tab-${item.id}`}
                      onClick={() => { setTab(item.id); setMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                        active
                          ? "bg-primary/15 text-primary"
                          : hl
                            ? "bg-gradient-to-r from-amber-400/20 to-transparent text-amber-300 font-semibold border border-amber-400/30 hover:from-amber-400/30"
                            : "text-primary/80 hover:bg-primary/5 hover:text-primary"
                      }`}
                    >
                      <Icon size={18} strokeWidth={hl ? 2.2 : 1.8} className={hl && !active ? "text-amber-300" : ""} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="flex-shrink-0 border-t border-white/5 px-3 py-4 space-y-1">
                <button
                  data-testid="menu-chemin-btn"
                  onClick={() => { setMenuOpen(false); navigate("/chemin-de-soie"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-primary/80 hover:bg-primary/5 hover:text-primary transition-colors"
                >
                  <Feather size={18} strokeWidth={1.8} />
                  <span className="font-medium">{t("nav.chemin")}</span>
                </button>
                <button
                  data-testid="menu-profile-btn"
                  onClick={() => { setMenuOpen(false); navigate("/profil"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-primary/80 hover:bg-primary/5 hover:text-primary transition-colors"
                >
                  <Settings size={18} strokeWidth={1.8} />
                  <span className="font-medium">{t("atelier.profile.title")}</span>
                </button>
                {user?.role === "admin" && (
                  <button
                    data-testid="menu-admin-btn"
                    onClick={() => { setMenuOpen(false); navigate("/admin"); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-primary/80 hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    <Megaphone size={18} strokeWidth={1.8} />
                    <span className="font-medium">Dashboard</span>
                    {unreadMsgs > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                        {unreadMsgs > 9 ? "9+" : unreadMsgs}
                      </span>
                    )}
                  </button>
                )}
                <button
                  data-testid="menu-logout-btn"
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-primary/80 hover:bg-primary/5 hover:text-primary transition-colors"
                >
                  <LogOut size={18} strokeWidth={1.8} />
                  <span className="font-medium">Quitter</span>
                </button>
                <div className="px-4 pt-3">
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>


      <main className="flex-1 min-h-0 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            {tab === "accueil" && <Accueil streak={streak} onNavigate={setTab} onExplore={explore} pinnedTools={pinnedTools} onUnpin={togglePin} />}
            {tab === "fil" && <FilDeCuriosite pendingPrompt={pendingPrompt} />}
            {tab === "boussole" && <Boussole onExplore={explore} />}
            {tab === "domaines" && <Domaines onExplore={explore} starCounts={starCounts} />}
            {tab === "constellation" && <Constellation onExplore={explore} />}
            {tab === "cocon" && (
              <div className="h-full overflow-y-auto px-5 sm:px-8 py-8">
                <div className="max-w-3xl mx-auto glass rounded-2xl p-6 sm:p-8">
                  <CoconHistory />
                </div>
              </div>
            )}
            {tab === "carnet" && <Carnet />}
            {tab === "campfire" && <Campfire />}
            {tab === "duo" && <DuoCuriosite />}
            {tab === "cercle" && <CercleLaunch />}
            {tab === "deuxVoies" && <DeuxVoies onExplore={explore} />}
            {tab === "proces" && <ProcesIdees onExplore={explore} />}
            {tab === "doute" && <FeuDuDoute />}
            {tab === "verites" && <TroisAges onExplore={explore} />}
            {tab === "expedition" && <Explorateur onExplore={explore} />}
            {tab === "jeux" && <JeuDeLEsprit onDiscuss={(txt) => { setPendingPrompt({ text: txt, ts: Date.now() }); setTab("fil"); }} />}
            {tab === "podcasts" && <MesPodcasts />}
            {tab === "abonnement" && <MonAbonnement />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
